import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  RouterProvider,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { requireGuest, requireSession } from "./auth/guards";
import { BuilderWizard } from "./components/BuilderWizard";
import { CaptureViewer } from "./components/CaptureViewer";
import { RunConsole } from "./components/RunConsole";
import { DashboardPage } from "./pages/DashboardPage";
import { AccountPage } from "./pages/AccountPage";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { SettingsVaultPage } from "./pages/SettingsVaultPage";
import { UsagePage } from "./pages/UsagePage";
import { TaskJinnLogo } from "./components/TaskJinnLogo";
import { useBuilderStore } from "./store";

export function AppShell() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    const element = document.getElementById(id);
    if (element) {
      e.preventDefault();
      element.scrollIntoView({ behavior: "smooth" });
      window.history.pushState(null, "", `#${id}`);
    }
  };

  // The builder store still carries a demo template/draft id (Phase 5 follow-up), but the tenant
  // must come from the authenticated session rather than a hardcoded account.
  useEffect(() => {
    if (!session?.accountId) return;
    useBuilderStore.setState({
      accountId: session.accountId,
      role: session.role ?? "run_only",
    });
  }, [session?.accountId, session?.role]);

  return (
    <div className="min-h-screen bg-[var(--tj-bg)] text-[var(--tj-fg)]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[var(--bg-parchment)]/95 border-b border-[var(--border-warm)]">
        <nav className="tj-container flex items-center justify-between py-2.5 sm:py-3.5" aria-label="Primary Navigation">
          {/* Brand Logo */}
          <Link
            to="/"
            className="group flex items-center gap-2 sm:gap-2.5 no-underline shrink-0"
            onClick={() => setMobileMenuOpen(false)}
          >
            <TaskJinnLogo className="w-7 h-7 sm:w-8 sm:h-8" />
            <div className="flex flex-col">
              <span className="font-extrabold text-lg sm:text-xl tracking-tight text-[var(--tj-fg)] leading-none">
                TaskJinn
              </span>
              <span className="hidden sm:inline-block text-[9px] tracking-wider uppercase font-semibold text-[var(--tj-muted)] mt-0.5">
                Human-Supervised Automation
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3">
            {session ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium hover:text-[var(--accent-terracotta)] px-2.5 py-1">Dashboard</Link>
                <Link to="/account" className="text-sm font-medium hover:text-[var(--accent-terracotta)] px-2.5 py-1">Account</Link>
                <Link to="/settings" className="text-sm font-medium hover:text-[var(--accent-terracotta)] px-2.5 py-1">Secrets</Link>
                <Link to="/usage" className="text-sm font-medium hover:text-[var(--accent-terracotta)] px-2.5 py-1">Usage</Link>
                <span className="tj-badge tj-badge-amber">
                  {session.email ?? session.role ?? "member"}
                </span>
                <button
                  type="button"
                  className="tj-btn tj-btn-secondary text-sm ml-2"
                  onClick={() => {
                    signOut();
                    void navigate({ to: "/" });
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-5 mr-3 text-sm text-[var(--tj-muted)] font-medium">
                  <a
                    href="/#features"
                    onClick={scrollToSection("features")}
                    className="hover:text-[var(--tj-fg)] transition-colors py-1 cursor-pointer"
                  >
                    Features
                  </a>
                  <a
                    href="/#solutions"
                    onClick={scrollToSection("solutions")}
                    className="hover:text-[var(--tj-fg)] transition-colors py-1 cursor-pointer"
                  >
                    Solutions
                  </a>
                </div>
                <Link to="/login" className="tj-btn tj-btn-secondary text-sm">
                  Login
                </Link>
                <Link to="/login" className="tj-btn tj-btn-primary text-sm shadow-xs">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Right Bar (Single Line, Never Wraps) */}
          <div className="flex md:hidden items-center gap-1.5 sm:gap-2">
            {session ? (
              <>
                <Link
                  to="/dashboard"
                  className="tj-btn tj-btn-primary text-xs px-3 py-1.5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  aria-label="Toggle navigation menu"
                  aria-expanded={mobileMenuOpen}
                  className="p-1.5 rounded-lg border border-[var(--border-warm)] bg-[var(--bg-card)] text-[var(--tj-fg)] hover:border-orange-300"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-xs sm:text-sm font-semibold text-[var(--tj-fg)] hover:text-[var(--accent-terracotta)] px-2 py-1.5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/login"
                  className="tj-btn tj-btn-primary text-xs font-semibold px-3 py-1.5 shadow-xs"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
                <button
                  type="button"
                  aria-label="Toggle navigation menu"
                  aria-expanded={mobileMenuOpen}
                  className="p-1.5 rounded-lg border border-[var(--border-warm)] bg-[var(--bg-card)] text-[var(--tj-fg)] hover:border-orange-300"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </>
            )}
          </div>
        </nav>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[var(--border-warm)] bg-[var(--bg-parchment)]/98 backdrop-blur-lg px-4 py-3.5 space-y-2.5 shadow-lg">
            {session ? (
              <div className="flex flex-col space-y-1">
                <div className="text-xs font-semibold text-[var(--tj-muted)] px-3 py-1.5 border-b border-[var(--border-warm)] mb-1">
                  Signed in as <span className="text-[var(--tj-fg)] font-bold">{session.email ?? session.role ?? "member"}</span>
                </div>
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 rounded-lg text-sm font-medium hover:bg-orange-50 hover:text-[var(--accent-terracotta)]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/account"
                  className="block px-3 py-2 rounded-lg text-sm font-medium hover:bg-orange-50 hover:text-[var(--accent-terracotta)]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Account &amp; Devices
                </Link>
                <Link
                  to="/settings"
                  className="block px-3 py-2 rounded-lg text-sm font-medium hover:bg-orange-50 hover:text-[var(--accent-terracotta)]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Secrets Vault
                </Link>
                <Link
                  to="/usage"
                  className="block px-3 py-2 rounded-lg text-sm font-medium hover:bg-orange-50 hover:text-[var(--accent-terracotta)]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Usage &amp; Billing
                </Link>
                <div className="pt-2 border-t border-[var(--border-warm)] mt-1">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-rose-700 hover:bg-rose-50"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      signOut();
                      void navigate({ to: "/" });
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col space-y-2">
                <a
                  href="/#features"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-[var(--tj-fg)] hover:bg-orange-50 hover:text-[var(--accent-terracotta)]"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    scrollToSection("features")(e);
                  }}
                >
                  Features &amp; Capabilities
                </a>
                <a
                  href="/#solutions"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-[var(--tj-fg)] hover:bg-orange-50 hover:text-[var(--accent-terracotta)]"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    scrollToSection("solutions")(e);
                  }}
                >
                  Solutions for Teams
                </a>
                <div className="pt-2.5 border-t border-[var(--border-warm)] flex gap-2">
                  <Link
                    to="/login"
                    className="tj-btn tj-btn-secondary text-sm flex-1 text-center justify-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/login"
                    className="tj-btn tj-btn-primary text-sm flex-1 text-center justify-center shadow-xs"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </header>
      <Outlet />
    </div>
  );
}

const rootRoute = createRootRoute({ component: AppShell });

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

interface LoginSearch {
  redirect?: string;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: () => requireGuest(),
  component: LoginPage,
});

const builderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates/$templateId/builder",
  validateSearch: (search: Record<string, unknown>) => ({
    step: typeof search.step === "string" ? search.step : undefined,
    connector: typeof search.connector === "string" ? search.connector : undefined,
  }),
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: BuilderRoute,
});

function BuilderRoute() {
  const search = useSearch({ from: builderRoute.id });
  const { session } = useAuth();
  if (search.step === "finalize" && session?.role === "run_only") {
    return (
      <main className="tj-container max-w-xl py-10" data-testid="finalize-denied">
        <div className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-6">
          <span className="tj-badge tj-badge-amber">Owner/Admin only</span>
          <h1 className="serif-heading mt-3 text-2xl">Finalizing is restricted</h1>
          <p className="mt-2 text-sm text-[var(--tj-muted)]">
            Your account role is <strong>Run only</strong>. You can edit a draft and propose a
            finalize, which creates a change request for an Owner or Admin to review.
          </p>
          <Link
            to="/templates/$templateId/builder"
            params={{ templateId: "demo" }}
            search={{ step: undefined, connector: undefined }}
            className="tj-btn tj-btn-primary mt-4 inline-block"
          >
            Back to the builder
          </Link>
        </div>
      </main>
    );
  }
  return <BuilderWizard />;
}

const capturesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$taskId/captures",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: () => {
    const params = capturesRoute.useParams();
    return <CaptureViewer taskRunId={params.taskId} />;
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: DashboardPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: AccountPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: SettingsVaultPage,
});

const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/usage",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: UsagePage,
});

const taskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$taskId",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: () => {
    const params = taskRoute.useParams();
    return <RunConsole taskId={params.taskId} />;
  },
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  loginRoute,
  privacyRoute,
  termsRoute,
  builderRoute,
  capturesRoute,
  dashboardRoute,
  accountRoute,
  settingsRoute,
  usageRoute,
  taskRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
